/**
 * 设计系统基础组件 —— DESIGN-SYSTEM.md §6 的落地。
 *
 * 两条不可妥协：按钮 ≥44px（主操作 48px）、文字色不浅于 --color-ink-3。
 */

import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react'
import { createPortal } from 'react-dom'
import { clsx } from 'clsx'
import { Loader2, X, AlertTriangle, Check } from 'lucide-react'

export const cx = clsx

// ─────────────────────────── Button ───────────────────────────

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
type Size = 'lg' | 'md' | 'sm'

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:hover:bg-brand-600',
  secondary:
    'bg-white text-ink border border-line-strong hover:bg-sunken',
  ghost: 'bg-transparent text-ink hover:bg-sunken',
  danger: 'bg-bad text-white hover:brightness-110',
  link: 'bg-transparent text-brand-700 underline underline-offset-2 hover:text-brand-800 px-0',
}

const SIZE: Record<Size, string> = {
  lg: 'h-12 px-5 text-[17px]',
  md: 'h-11 px-4 text-[17px]',
  sm: 'h-9 px-3 text-[15px]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  block?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading, icon, block, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-md font-semibold',
        'transition-colors duration-100 disabled:opacity-45 disabled:cursor-not-allowed',
        VARIANT[variant],
        variant !== 'link' && SIZE[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {/* loading 时保持宽度不变，避免布局跳动 */}
      {loading ? <Loader2 size={18} className="animate-spin" /> : icon}
      {children}
    </button>
  )
})

// ─────────────────────────── Field / Input ───────────────────────────

/**
 * 表单字段。
 * 标签用真正的 <label> 并与控件关联（NFR-3.5），错误用 aria-describedby 关联 ——
 * 屏幕阅读器要能读出「密码，电话至少 9 位数字」，而不是一个无名输入框。
 * 关联通过 cloneElement 注入 id，调用方不必手动传。
 */
export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label?: string
  hint?: string
  error?: string
  required?: boolean
  children: ReactNode
}) {
  const id = useId()
  const controlId = `${id}-control`
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined

  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? controlId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label htmlFor={controlId} className="text-[15px] font-semibold text-ink">
          {label}
          {required && <span className="text-bad ml-0.5">*</span>}
        </label>
      )}
      {control}
      {error ? (
        <span id={`${id}-error`} className="flex items-center gap-1.5 text-[15px] text-bad">
          <AlertTriangle size={15} /> {error}
        </span>
      ) : hint ? (
        <span id={`${id}-hint`} className="text-[15px] text-ink-3">
          {hint}
        </span>
      ) : null}
    </div>
  )
}

const inputBase =
  'h-12 w-full rounded-md border bg-white px-3.5 text-[17px] text-ink ' +
  'placeholder:text-ink-3 transition-colors ' +
  'focus:outline-none focus:border-brand-600 focus:shadow-[var(--focus-ring)] ' +
  'disabled:bg-sunken disabled:text-ink-3'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }>(
  function Input({ className, invalid, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cx(
          inputBase,
          invalid ? 'border-bad' : 'border-line-strong',
          className,
        )}
        {...rest}
      />
    )
  },
)

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cx(
          inputBase,
          'h-auto min-h-24 border-line-strong py-2.5 leading-relaxed',
          className,
        )}
        {...rest}
      />
    )
  },
)

/** 金额输入：RM 前缀、右对齐、等宽数字、数字键盘。 */
export function MoneyInput({
  value,
  onChange,
  invalid,
  autoFocus,
  placeholder = '0.00',
  // Field 会注入这些，必须转发到真正的 input 上，否则 <label for> 指向空
  id,
  'aria-describedby': describedBy,
  'aria-required': ariaRequired,
}: {
  value: string
  onChange: (v: string) => void
  invalid?: boolean
  autoFocus?: boolean
  placeholder?: string
  id?: string
  'aria-describedby'?: string
  'aria-required'?: boolean
}) {
  return (
    <div
      className={cx(
        'flex h-12 items-center rounded-md border bg-white transition-colors',
        'focus-within:border-brand-600 focus-within:shadow-[var(--focus-ring)]',
        invalid ? 'border-bad' : 'border-line-strong',
      )}
    >
      <span className="px-3.5 text-[17px] font-semibold text-ink-2 select-none">RM</span>
      <input
        id={id}
        aria-describedby={describedBy}
        aria-required={ariaRequired}
        aria-invalid={invalid || undefined}
        inputMode="decimal"
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="tnum h-full flex-1 rounded-r-md bg-transparent pr-3.5 text-right text-[17px] focus:outline-none"
      />
    </div>
  )
}

// ─────────────────────────── Segmented / Chip ───────────────────────────

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  id,
  'aria-describedby': describedBy,
}: {
  options: Array<{ value: T; label: string }>
  value: T | null
  onChange: (v: T) => void
  ariaLabel?: string
  id?: string
  'aria-describedby'?: string
}) {
  return (
    <div
      id={id}
      role="radiogroup"
      // 组本身有 aria-label，即便外层 <label for> 指向的是容器也能被正确朗读
      aria-label={ariaLabel}
      aria-describedby={describedBy}
      className="inline-flex rounded-md border border-line-strong bg-white p-1 gap-1"
    >
      {options.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cx(
            'h-10 min-w-20 rounded-sm px-4 text-[16px] font-semibold transition-colors',
            value === o.value
              ? 'bg-brand-100 text-brand-700'
              : 'text-ink-2 hover:bg-sunken',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function Chip({
  active,
  onClick,
  children,
}: {
  active?: boolean
  onClick?: () => void
  children: ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={cx(
        'h-10 rounded-full border px-4 text-[15px] font-semibold transition-colors',
        active
          ? 'border-brand-600 bg-brand-100 text-brand-700'
          : 'border-line-strong bg-white text-ink-2 hover:bg-sunken',
      )}
    >
      {children}
    </button>
  )
}

// ─────────────────────────── Card / Badge ───────────────────────────

export function Card({
  title,
  action,
  className,
  bodyClassName,
  children,
}: {
  title?: ReactNode
  action?: ReactNode
  className?: string
  bodyClassName?: string
  children: ReactNode
}) {
  return (
    <section
      className={cx(
        'rounded-md border border-line bg-white shadow-xs',
        className,
      )}
    >
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4 sm:px-6">
          <h2 className="text-[19px] font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className={cx('px-5 py-4 sm:px-6 sm:py-5', bodyClassName)}>{children}</div>
    </section>
  )
}

type Tone = 'neutral' | 'brand' | 'ok' | 'warn' | 'bad' | 'dark' | 'info'

const TONE: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-2 border-line',
  brand: 'bg-brand-100 text-brand-700 border-brand-200',
  ok: 'bg-ok-bg text-ok border-ok-line',
  warn: 'bg-warn-bg text-warn border-warn-line',
  bad: 'bg-bad-bg text-bad border-bad-line',
  info: 'bg-info-bg text-info border-info-line',
  dark: 'bg-ink-2 text-white border-ink-2',
}

export function Badge({
  tone = 'neutral',
  icon,
  children,
}: {
  tone?: Tone
  icon?: ReactNode
  children: ReactNode
}) {
  return (
    <span
      className={cx(
        'inline-flex h-[26px] shrink-0 items-center gap-1 rounded-full border px-2.5 text-[13px] font-semibold',
        TONE[tone],
      )}
    >
      {icon}
      {children}
    </span>
  )
}

/** 工单状态 → 徽章色。逾期用红色，所以「可取件」用绿而非红。 */
export function caseStatusTone(status: string): Tone {
  return (
    (
      {
        DRAFT: 'neutral',
        IN_PROGRESS: 'brand',
        READY: 'ok',
        DELIVERED: 'dark',
        CLOSED: 'neutral',
        ON_HOLD: 'warn',
        CANCELLED: 'neutral',
      } as Record<string, Tone>
    )[status] ?? 'neutral'
  )
}

export function apptStatusTone(status: string): Tone {
  return (
    (
      {
        BOOKED: 'neutral',
        CONFIRMED: 'brand',
        ARRIVED: 'ok',
        DONE: 'dark',
        NO_SHOW: 'bad',
        CANCELLED: 'neutral',
      } as Record<string, Tone>
    )[status] ?? 'neutral'
  )
}

// ─────────────────────────── Empty / Skeleton / Banner ───────────────────────────

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="mx-auto flex max-w-90 flex-col items-center gap-3 py-10 text-center">
      {icon && <div className="text-ink-3">{icon}</div>}
      <h3 className="text-[19px] font-semibold">{title}</h3>
      {description && <p className="text-[15px] text-ink-2">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('gb-skeleton', className)} />
}

export function Banner({
  tone = 'warn',
  icon,
  children,
  action,
}: {
  tone?: 'warn' | 'bad' | 'info'
  icon?: ReactNode
  children: ReactNode
  action?: ReactNode
}) {
  const styles = {
    warn: 'bg-warn-bg border-warn-line text-warn',
    bad: 'bg-bad-bg border-bad-line text-bad',
    info: 'bg-info-bg border-info-line text-info',
  }[tone]

  return (
    <div
      role="alert"
      className={cx(
        'flex items-center gap-3 border-b px-4 py-3 text-[16px] font-medium sm:px-6',
        styles,
      )}
    >
      {icon}
      <span className="flex-1">{children}</span>
      {action}
    </div>
  )
}

// ─────────────────────────── Toast ───────────────────────────

interface ToastMsg {
  id: number
  text: string
  tone: 'ok' | 'bad'
  undo?: () => void
}

const ToastCtx = createContext<{
  show: (text: string, opts?: { tone?: 'ok' | 'bad'; undo?: () => void }) => void
}>({ show: () => {} })

export const useToast = () => useContext(ToastCtx)

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastMsg | null>(null)

  const show = useCallback(
    (text: string, opts?: { tone?: 'ok' | 'bad'; undo?: () => void }) => {
      // 同时最多一条，新的替换旧的 —— 老板不需要看消息堆叠
      setToast({ id: Date.now(), text, tone: opts?.tone ?? 'ok', undo: opts?.undo })
    },
    [],
  )

  useEffect(() => {
    if (!toast) return
    // 可撤销的多留 2 秒
    const ms = toast.undo ? 5000 : 3000
    const t = setTimeout(() => setToast(null), ms)
    return () => clearTimeout(t)
  }, [toast])

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastCtx.Provider value={value}>
      {children}
      {toast &&
        createPortal(
          <div
            role={toast.tone === 'bad' ? 'alert' : 'status'}
            className="gb-anim-toast fixed inset-x-4 bottom-6 z-100 mx-auto flex max-w-105 items-center gap-3 rounded-md bg-ink px-4 py-3.5 text-white shadow-lg"
          >
            <span className="flex-1 text-[16px]">{toast.text}</span>
            {toast.undo && (
              <button
                onClick={() => {
                  toast.undo?.()
                  setToast(null)
                }}
                className="shrink-0 font-semibold text-brand-200 underline underline-offset-2"
              >
                撤销
              </button>
            )}
          </div>,
          document.body,
        )}
    </ToastCtx.Provider>
  )
}

// ─────────────────────────── Drawer ───────────────────────────

export function Drawer({
  open,
  onClose,
  title,
  footer,
  children,
  width = 'md',
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  footer?: ReactNode
  children: ReactNode
  width?: 'md' | 'lg'
}) {
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="gb-anim-overlay absolute inset-0 bg-[rgb(28_25_23/0.4)]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cx(
          'gb-anim-drawer relative flex h-full w-full flex-col bg-white shadow-lg',
          'sm:rounded-l-lg',
          width === 'lg' ? 'sm:w-[560px]' : 'sm:w-[480px]',
        )}
      >
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-5 py-4">
          <h2 id={titleId} className="text-[22px] font-semibold">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="关闭"
            className="flex size-11 items-center justify-center rounded-md text-ink-2 hover:bg-sunken"
          >
            <X size={22} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-5">{children}</div>

        {footer && (
          <footer className="flex shrink-0 flex-col-reverse gap-3 border-t border-line px-5 py-4 sm:flex-row sm:justify-end">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  )
}

// ─────────────────────────── Modal（仅用于确认与破坏性操作）───────────────────────────

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer: ReactNode
}) {
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      <div
        className="gb-anim-overlay absolute inset-0 bg-[rgb(28_25_23/0.4)]"
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="gb-anim-modal relative w-full max-w-100 rounded-lg bg-white p-6 shadow-lg"
      >
        <h2 id={titleId} className="text-[22px] font-semibold">
          {title}
        </h2>
        <div className="mt-3 text-[17px] text-ink-2">{children}</div>
        <div className="mt-6 flex justify-end gap-3">{footer}</div>
      </div>
    </div>,
    document.body,
  )
}

// ─────────────────────────── 杂项 ───────────────────────────

export function Avatar({ name, size = 40 }: { name: string; size?: number }) {
  // 姓名哈希映射到 6 个低饱和色，同一个人颜色恒定
  const palette = ['#0D9488', '#0369A1', '#B45309', '#15803D', '#7C3AED', '#BE185D']
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  const bg = palette[h % palette.length]!
  // 马来名取第一个词首字母，中文名取姓
  const initials = /[一-龥]/.test(name)
    ? name.slice(0, 1)
    : name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] ?? '')
        .join('')
        .toUpperCase()

  return (
    <span
      aria-hidden
      style={{ width: size, height: size, background: `${bg}1a`, color: bg, fontSize: size * 0.38 }}
      className="inline-flex shrink-0 items-center justify-center rounded-full font-bold"
    >
      {initials}
    </span>
  )
}

export function CheckMark({ size = 18 }: { size?: number }) {
  return (
    <svg
      className="gb-check"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={3}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

export { Check, AlertTriangle }
